import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useLeaderboardClubs,
  type GlobalLeaderboardTab,
} from "@/shared/hooks";
import { isMembershipRequiredError } from "@/shared/api/client";
import {
  LEADERBOARD_TAB_DESCRIPTIONS,
  LEADERBOARD_TAB_TEXTS,
} from "@/shared/texts/leaderboard";
import { BottomNav } from "@/shared/ui/BottomNav";
import { Button } from "@/shared/ui/Button";
import { EmptyState } from "@/shared/ui/EmptyState";
import { PageHeader } from "@/shared/ui/PageHeader";
import { ScreenLayout } from "@/shared/ui/ScreenLayout";
import { Skeleton } from "@/shared/ui/Skeleton";
import type { LeaderboardClub } from "@/shared/types";

/** Pravki §7 v3.2 (ребрендинг): 3 категории (stat — per-habit фича). */
const SECTIONS: GlobalLeaderboardTab[] = ["streak", "catches", "shame"];

export function GlobalLeaderboardPage() {
  // Все аккордеоны закрыты по умолчанию. Юзер кликает чтобы открыть.
  const [openTab, setOpenTab] = useState<GlobalLeaderboardTab | null>(null);
  // Запрос только для открытой секции. Ответ содержит `metric_label`
  // («Серии»/«Охотники»/«Лентяи») — бэкенд остаётся источником правды
  // для заголовка секции; локальный лейбл — fallback.
  const { data, isLoading, isError, error } = useLeaderboardClubs(
    openTab ?? "streak",
    { enabled: openTab !== null },
  );

  return (
    <ScreenLayout>
      <PageHeader title="Рейтинг" />

      <div className="space-y-2">
        {SECTIONS.map((section) => {
          const local = LEADERBOARD_TAB_TEXTS[section];
          const isOpen = openTab === section;
          // Лейбл из ответа бэкенда доступен только для открытой секции
          // (ответ приходит вместе со списком клубов).
          const label = (isOpen && data?.metric_label) || local.label;
          return (
            <Accordion
              key={section}
              id={section}
              label={label}
              emoji={local.emoji}
              description={LEADERBOARD_TAB_DESCRIPTIONS[section]}
              isOpen={isOpen}
              onToggle={() =>
                setOpenTab((prev) => (prev === section ? null : section))
              }
              isLoading={isLoading}
              isError={isError}
              error={error}
              clubs={data?.clubs}
            />
          );
        })}
      </div>

      <BottomNav />
    </ScreenLayout>
  );
}

function Accordion({
  id,
  label,
  emoji,
  description,
  isOpen,
  onToggle,
  isLoading,
  isError,
  error,
  clubs,
}: {
  id: GlobalLeaderboardTab;
  label: string;
  emoji: string;
  /** Что показывает метрика секции (общий текст с клубным лидербордом). */
  description: string;
  isOpen: boolean;
  onToggle: () => void;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  clubs: LeaderboardClub[] | undefined;
}) {
  return (
    <section className="overflow-hidden rounded-card border border-white/10 bg-surface">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-white/5"
        aria-expanded={isOpen}
      >
        <span className="text-xl" aria-hidden="true">
          {emoji}
        </span>
        <span className="flex-1 text-base font-semibold text-text">{label}</span>
        <span
          aria-hidden="true"
          className={`text-muted transition-transform ${isOpen ? "rotate-180" : ""}`}
        >
          ▾
        </span>
      </button>
      {isOpen && (
        <AccordionContent
          id={id}
          description={description}
          isLoading={isLoading}
          isError={isError}
          error={error}
          clubs={clubs}
        />
      )}
    </section>
  );
}

function AccordionContent({
  id,
  description,
  isLoading,
  isError,
  error,
  clubs,
}: {
  id: GlobalLeaderboardTab;
  description: string;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  clubs: LeaderboardClub[] | undefined;
}) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="space-y-2 border-t border-white/5 p-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (isError) {
    // Этап 7, 7.11: 403 membership_required = у юзера нет ни одного клуба.
    if (isMembershipRequiredError(error)) {
      return (
        <div className="border-t border-white/5 p-3">
          <EmptyState
            icon="🔒"
            title="Вступи в клуб, чтобы увидеть рейтинг"
            description="Рейтинг показывается по клубам, в которых ты состоишь."
          />
        </div>
      );
    }
    return (
      <div className="border-t border-white/5 p-3">
        <EmptyState
          icon="⚠️"
          title="Не удалось загрузить"
          description={String(error)}
        />
      </div>
    );
  }

  if (!clubs || clubs.length === 0) {
    return (
      <div className="border-t border-white/5 p-3">
        <EmptyState
          icon="🏪"
          title="Нет клубов"
          description="Вступи в любой клуб — здесь будет рейтинг."
          action={
            <Button
              onClick={() => navigate("/marketplace")}
              className="mt-3 px-4 py-2 text-sm"
            >
              Выбрать клуб
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="border-t border-white/5">
      {/* Что показывает метрика секции — та же подпись, что на клубном
          лидерборде (например, «Текущая серия: сколько дней подряд …»). */}
      <p className="px-4 pt-3 text-xs text-muted">{description}</p>
      <ul>
        {clubs.map((club) => (
          <ClubListItem
            key={club.habit_id}
            club={club}
            onClick={() => navigate(`/habits/${club.habit_id}/leaderboard?tab=${id}`)}
          />
        ))}
      </ul>
    </div>
  );
}

function ClubListItem({
  club,
  onClick,
}: {
  club: LeaderboardClub;
  onClick: () => void;
}) {
  return (
    <li className="border-b border-white/5 last:border-b-0">
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left transition-colors hover:bg-white/5"
      >
        <span className="flex-1 truncate text-sm text-text">〖{club.title}〗</span>
        <span className="text-xs uppercase tracking-wide text-muted">
          {club.members_count} УЧАСТНИКОВ
        </span>
      </button>
    </li>
  );
}
